import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { NfTableComponent } from '../../../components/nf-table/nf-table';
import { ContactHistory, LeadsDetailsService } from '../../../services/leads-details-service.service';
import { LeadInfo, LeadHistoricoSimulacoes, Simulacao } from '../../../interfaces/leads-info-history.interface';
import { LocalStorageUtil } from '../../../utils/local-storage.util';
import { ContratoCdcFactory } from '../../../utils/contrato-cdc.factory';
import { DateCalculatorService } from '../../../services/date-calculator.service';
import { RenovaPlusService } from '../../../services/renova-plus.service';
import { runtimeLogger } from '../../../utils/runtime-logger.util';

@Component({
  selector: 'app-leads-history-info',
  standalone: true,
  imports: [CommonModule, NfTableComponent],
  templateUrl: './leads-history-info.html',
  styleUrls: ['./leads-history-info.scss']
})
export class LeadsHistoryInfo implements OnInit {
  // ADICIONAR: leadId da rota
  leadId: string | null = null;

  // ===== LOADINGS INDEPENDENTES =====
  loading = true;
  contactLoading = true;
  simulationLoading = true; // SEPARADO do bancoLoading

  // ===== ERROS INDEPENDENTES =====
  contactError = false;
  simulationError = false; // SEPARADO do bancoError

  // ===== TIPOS DE ERRO =====
  contactErrorType: 'LEAD_NOT_FOUND' | 'FORBIDDEN' | 'GENERIC_ERROR' | 'NO_CONTACTS' | null = null;
  simulationErrorType: 'LEAD_NOT_FOUND' | 'UNAUTHORIZED' | 'API_ERROR' | 'NO_SIMULATIONS' | null = null;

  // ===== DADOS DAS SEÇÕES =====
  // Histórico de Contato
  contactHistoryColumns = ['Data', 'Vendedor', 'Campanha', 'Observação', 'Status', 'Retornar Em'];
  contactHistoryData: ContactHistory[] = [];

  // Histórico de Relacionamento (vazio)
  relationshipHistoryColumns = ['Data', 'Tipo', 'Descrição'];
  relationshipHistoryData: any[] = [];

  // Histórico de Financiamento
  financingHistoryColumns = ['Inicio', 'Fim', 'Status', 'Chassi', 'Vl. Veículo', 'Vl. Parcela', 'Prazo'];
  financingHistoryData: any[] = [];

  // Histórico de Simulação
  simulationHistoryColumns = ['Data da simulação', 'Origem da simulação', 'Produto de interesse', 'Marca', 'Modelo', 'Versão', 'Entrada', 'Prazo'];
  simulationHistoryData: any[] = [];

  // Histórico de Consórcio (vazio)
  consortiumHistoryColumns = ['Data', 'Tipo', 'Status', 'Valor'];
  consortiumHistoryData: any[] = [];

  // Histórico de Assinatura (vazio)
  signatureHistoryColumns = ['Data', 'Documento', 'Status'];
  signatureHistoryData: any[] = [];

  // Dados das APIs do banco
  leadInfo: LeadInfo | null = null;
  historicoSimulacoes: LeadHistoricoSimulacoes | null = null;

  constructor(
    private leadsDetailsService: LeadsDetailsService,
    private renovaPlusService: RenovaPlusService,
    private dateCalculatorService: DateCalculatorService,
    private route: ActivatedRoute,
    private router: Router

  ) { }

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.leadId = params['leadId'] || null;
      runtimeLogger.debug('📋 LeadId capturado da rota:', this.leadId);

      if (this.leadId) {
        // Iniciar os dois carregamentos em paralelo
        this.loadContactHistory();
        this.loadSimulationData(); // NOME ALTERADO para ficar claro
      } else {
        console.error('❌ LeadId não encontrado na rota');
        this.contactError = true;
        this.contactErrorType = 'LEAD_NOT_FOUND';
        this.contactLoading = false;

        this.simulationError = true;
        this.simulationErrorType = 'LEAD_NOT_FOUND';
        this.simulationLoading = false;
        this.checkOverallLoading();
      }
    });

    this.route.queryParams.subscribe(queryParams => {
      if (!this.leadId && queryParams['idLead']) {
        this.leadId = queryParams['idLead'];
        runtimeLogger.debug('📋 LeadId capturado dos query params:', this.leadId);

        if (this.leadId) {
          this.loadContactHistory();
          this.loadSimulationData(); // NOME ALTERADO
        }
      }
    });
  }

  // ===== MÉTODO PARA CARREGAR DADOS DE SIMULAÇÃO =====
  loadSimulationData() {
    this.simulationLoading = true;
    this.simulationError = false;
    this.simulationErrorType = null;

    const leadIdFromRoute = this.leadId;

    if (!leadIdFromRoute) {
      console.error('❌ Não foi possível carregar dados de simulação: leadId não disponível');
      this.simulationError = true;
      this.simulationErrorType = 'LEAD_NOT_FOUND';
      this.simulationLoading = false;
      this.useMockedSimulationData();
      return;
    }

    // Buscar detalhes do lead para obter o CPF
    this.leadsDetailsService.getLeadDetails(leadIdFromRoute).subscribe({
      next: (details) => {
        const cpfCnpj = details.cpf || details.cdCpfCnpj; // Ajustar conforme retorno real da API
        const codigoConcessionaria = String(details.dn ?? ''); // Fallback ou extrair de details

        if (cpfCnpj) {
          // Remover caracteres não numéricos do CPF/CNPJ antes de chamar a API
          const cpfClean = cpfCnpj.replace(/\D/g, '');

          // Buscar contratos CDC filtrados/ordenados via Renova+. Backend agora fornece flag_renovacao
          this.renovaPlusService.verificarRenovaPlus(cpfClean).subscribe({
            next: (resultado) => {
              const contratosExibicao = resultado.contratosExibicao ?? [];

              this.financingHistoryData = contratosExibicao.map((contrato) =>
                ContratoCdcFactory.convertToHistoricoFinanciamento(contrato, this.dateCalculatorService)
              );

            },
            error: (err) => {
              this.financingHistoryData = [];
            }
          });

          // Buscar informações do lead no Banco VW
          this.leadsDetailsService.getLeadsInfo(LocalStorageUtil.getAccessToken(), cpfCnpj, codigoConcessionaria).subscribe({
            next: (leadInfoResponse) => {
              this.leadInfo = leadInfoResponse;
              runtimeLogger.debug('Lead Info recebido:', leadInfoResponse);

              // Buscar histórico de simulações
              this.leadsDetailsService.getLeadsHistoricoSimulacoes(
                LocalStorageUtil.getAccessToken(),
                leadInfoResponse.IdLeads,
                codigoConcessionaria
              ).subscribe({
                next: (historicoResponse) => {
                  this.historicoSimulacoes = historicoResponse;
                  runtimeLogger.debug('Histórico de simulações recebido:', historicoResponse);

                  // Apenas processar dados de simulação (ignorar Propostas, Contratos, StandAlones)
                  this.processarDadosSimulacao();
                  this.simulationLoading = false;
                  this.checkOverallLoading();
                },
                error: (error) => {
                  runtimeLogger.warn('Erro ao carregar histórico de simulações do banco:', error);
                  this.simulationError = true;
                  this.simulationErrorType = this.getSimulationErrorType(error);
                  this.simulationLoading = false;
                  this.checkOverallLoading();

                  // Usar dados mockados apenas para simulação
                  this.useMockedSimulationData();
                }
              });
            },
            error: (error) => {
              runtimeLogger.warn('Erro ao carregar informações do lead do banco:', error);
              this.simulationError = true;
              this.simulationErrorType = this.getSimulationErrorType(error);
              this.simulationLoading = false;
              this.checkOverallLoading();

              // Usar dados mockados apenas para simulação
              this.useMockedSimulationData();
            }
          });
        } else {
          runtimeLogger.warn('CPF não encontrado nos detalhes do lead');
          this.simulationLoading = false;
        }
      },
      error: (error) => {
        runtimeLogger.warn('⚠️ Erro ao carregar detalhes do lead:', error);
        this.simulationError = true;
        this.simulationErrorType = 'API_ERROR';
        this.simulationLoading = false;
        this.useMockedSimulationData();
      }
    });
  }

  // ===== MÉTODO PARA CARREGAR CONTATOS =====
  loadContactHistory() {
    this.contactLoading = true;
    this.contactError = false;
    this.contactErrorType = null;

    if (!this.leadId) {
      console.error('❌ LeadId não disponível para carregar histórico de contatos');
      this.contactError = true;
      this.contactErrorType = 'LEAD_NOT_FOUND';
      this.contactLoading = false;
      return;
    }

    runtimeLogger.debug('📞 Carregando histórico de contatos para lead:', this.leadId);

    this.leadsDetailsService.getContactHistory(this.leadId).subscribe({
      next: (data) => {
        runtimeLogger.debug('✅ Dados recebidos do histórico:', data);
          console.log('📊 Quantidade de contatos:', data?.length || 0);

        if (!data || data.length === 0) {
          this.contactError = true;
          this.contactErrorType = 'NO_CONTACTS';
          this.contactHistoryData = [];
          runtimeLogger.debug('⚠️ Nenhum contato encontrado');
        } else {
          this.contactHistoryData = [...data].sort((a, b) =>
            new Date(b.Data.split('/').reverse().join('-')).getTime() -
            new Date(a.Data.split('/').reverse().join('-')).getTime()
          );
          runtimeLogger.debug('✅ Contatos processados:', this.contactHistoryData);
        }
        this.contactLoading = false;
      },
      error: (error) => {
        console.error('❌ Erro ao carregar histórico de contatos:', error);
        this.contactError = true;

        if (error.message === 'LEAD_NOT_FOUND') {
          this.contactErrorType = 'LEAD_NOT_FOUND';
        } else if (error.message === 'FORBIDDEN') {
          this.contactErrorType = 'FORBIDDEN';
        } else {
          this.contactErrorType = 'GENERIC_ERROR';
        }

        this.contactLoading = false;
        this.contactHistoryData = [];
      }
    });
  }

  // ===== MÉTODOS AUXILIARES =====
  useMockedSimulationData() {
    this.simulationHistoryData = [
      {
        'Data da simulação': '06/09/2023',
        'Origem da simulação': 'Campanha',
        'Produto de interesse': 'Financiamento',
        'Marca': 'Volkswagen',
        'Modelo': 'T-Cross',
        'Versão': 'Comfortline 200 TSI',
        'Entrada': '30%',
        'Prazo': '24 meses'
      }
    ];
  }

  processarDadosSimulacao() {
    if (!this.historicoSimulacoes || this.historicoSimulacoes.Simulacoes.length === 0) {      
      runtimeLogger.debug('⚠️ Nenhuma simulação encontrada');
      this.simulationErrorType = 'NO_SIMULATIONS';
      this.simulationHistoryData = [];
      return;
    }

    const simulacoesFiltradas = this.historicoSimulacoes.Simulacoes.filter(simulacao =>
      simulacao.Vendedor && simulacao.Vendedor.toUpperCase().startsWith('SIMULA')
    );

    if (simulacoesFiltradas.length === 0) {
      runtimeLogger.debug('⚠️ Nenhuma simulação filtrada encontrada');
      this.simulationErrorType = 'NO_SIMULATIONS';
      this.simulationHistoryData = [];
      return;
    }

    const simulacoesOrdenadas = [...simulacoesFiltradas].sort((a, b) =>
      new Date(b.Data).getTime() - new Date(a.Data).getTime()
    );

    this.simulationHistoryData = simulacoesOrdenadas.map(simulacao => {
      const veiculoStr = String(simulacao.NomeVeiculo || '').trim();
      const partes = veiculoStr.split(' ');
      const modelo = partes.length > 0 ? partes[0] : '-';
      const versao = partes.length > 1 ? partes.slice(1).join(' ') : '-';
      const origemSimulacao = 
        simulacao.Vendedor === 'SIMULADOR_SEMPRENOVO'
        ? 'Sempre Novo'
        : simulacao.Vendedor;

      return {
        cpf: simulacao.Leads.CdCpfCnpj,
        'Data da simulação': this.formatarData(simulacao.Data),
        'Origem da simulação': origemSimulacao,
        'Produto de interesse': 'Financiamento',
        'Marca': simulacao.VeiculoNovo?.Marca || 'Volkswagen',
        'Modelo': modelo,
        'Versão': versao,
        'Entrada': simulacao.PercentualEntrada ? `${simulacao.PercentualEntrada}%` : '-',
        'Prazo': simulacao.Prazo ? `${simulacao.Prazo} meses` : '-'
      };
    })
      .reduce((acc, item) => {
        const key = `${item.cpf}|${item.Modelo}`;

        if (!acc.map.has(key)) {
          acc.map.set(key, true);
          acc.result.push(item);
        }

        return acc;
      }, { map: new Map<string, boolean>(), result: [] as any[] })
      .result;

    runtimeLogger.debug(`✅ ${this.simulationHistoryData.length} simulações processadas`);
  }

  getSimulationErrorType(error: any): 'LEAD_NOT_FOUND' | 'UNAUTHORIZED' | 'API_ERROR' | 'NO_SIMULATIONS' {
    if (error.message === 'LEAD_NOT_FOUND') {
      return 'LEAD_NOT_FOUND';
    } else if (error.message === 'UNAUTHORIZED') {
      return 'UNAUTHORIZED';
    }
    return 'API_ERROR';
  }

  checkOverallLoading() {
    this.loading = this.contactLoading || this.simulationLoading;
  }

  private buildDataSimulacao(): string {
    return new Date().toISOString().split('T')[0];
  }

  formatarData(dataString: string): string {
    if (!dataString) return '-';
    try {
      const data = new Date(dataString);
      return data.toLocaleDateString('pt-BR');
    } catch (error) {
      return dataString;
    }
  }

  // ===== MÉTODOS DE MENSAGEM DE ERRO =====
  getContactErrorMessage(): string {
    switch (this.contactErrorType) {
      case 'NO_CONTACTS':
        return 'Nenhum contato registrado para esta lead.';
      case 'LEAD_NOT_FOUND':
        return 'Lead não encontrada.';
      case 'FORBIDDEN':
        return 'Você não tem permissão para visualizar estas informações.';
      case 'GENERIC_ERROR':
        return 'Erro ao carregar histórico de contatos.';
      default:
        return '';
    }
  }

  getSimulationErrorMessage(): string {
    switch (this.simulationErrorType) {
      case 'NO_SIMULATIONS':
        return 'Nenhuma simulação registrada para esta lead.';
      case 'LEAD_NOT_FOUND':
        return 'Lead não encontrada no sistema financeiro.';
      case 'UNAUTHORIZED':
        return 'Falha na autenticação com o sistema financeiro.';
      case 'API_ERROR':
        return 'Erro na comunicação com o sistema financeiro.';
      default:
        return '';
    }
  }

  navigateToPage(page: number) {
    if (this.leadId) {
      // Usar rota com leadId
      const routesWithId = [
        `/leads/${this.leadId}/detalhes`,
        `/leads/${this.leadId}/leads-offers`,
        `/leads/${this.leadId}/leads-history`
      ];
      this.router.navigate([routesWithId[page]]);
    } else {
      // Fallback para rotas antigas
      const pageRoutes = ['/leads/leads-info', '/leads/leads-offers', '/leads/leads-history'];
      this.router.navigate([pageRoutes[page]]);
    }
  }
}
